import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../../lib/utils";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";


type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    manualBackdrop?: boolean;
    onBackdropClick?: () => void;
    hideClose?: boolean;
};

// Enhanced hook to robustly clean up pointer-events
function usePointerEventsCleanup(open?: boolean) {
    const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        // Setup MutationObserver to watch for pointer-events changes
        const observer = new MutationObserver(() => {
            // Check if dialog is closed but pointer-events is still set
            const dialogs = document.querySelectorAll('[role="dialog"]');
            const hasOpenDialogs = Array.from(dialogs).some(
                (dialog) => dialog.getAttribute('data-state') === 'open'
            );

            if (!hasOpenDialogs && document.body.style.pointerEvents === 'none') {
                if (process.env.NODE_ENV === 'development') console.log('[Dialog] Cleaning up stale pointer-events: none');
                document.body.style.pointerEvents = '';
            }
        });

        // Observe changes to body style attribute
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['style']
        });

        observerRef.current = observer;

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        // When dialog closes (open becomes false), ensure cleanup after animation
        if (open === false) {
            // Clear any existing timeout
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }

            // Set timeout to clean up after dialog close animation (300ms)
            cleanupTimeoutRef.current = setTimeout(() => {
                const dialogs = document.querySelectorAll('[role="dialog"]');
                const hasOpenDialogs = Array.from(dialogs).some(
                    (dialog) => dialog.getAttribute('data-state') === 'open'
                );

                if (!hasOpenDialogs) {
                    if (process.env.NODE_ENV === 'development') console.log('[Dialog] Cleaning up pointer-events after dialog close');
                    document.body.style.pointerEvents = '';
                }
            }, 350);
        }

        return () => {
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }
        };
    }, [open]);

    // Final cleanup on unmount
    useEffect(() => {
        return () => {
            // Use setTimeout to ensure this runs after Radix cleanup
            setTimeout(() => {
                const dialogs = document.querySelectorAll('[role="dialog"]');
                const hasOpenDialogs = Array.from(dialogs).some(
                    (dialog) => dialog.getAttribute('data-state') === 'open'
                );

                if (!hasOpenDialogs) {
                    if (process.env.NODE_ENV === 'development') console.log('[Dialog] Final cleanup on unmount');
                    document.body.style.pointerEvents = '';
                }
            }, 100);
        };
    }, []);
}

// Wrapper component for Dialog.Root that adds pointer-events cleanup
const DialogRoot: React.FC<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>> = (props) => {
    usePointerEventsCleanup(props.open);
    return <DialogPrimitive.Root {...props} />;
};

const Dialog = DialogRoot;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    DialogContentProps
>(({ className, children, manualBackdrop = false, onBackdropClick, hideClose = false, ...props }, ref) => (
    <DialogPrimitive.Portal>
        {/* Manual backdrop if requested */}
        {manualBackdrop && (
            <div
                className="fixed inset-0 z-50 bg-black/80 backdrop"
                aria-hidden="true"
                onClick={onBackdropClick}
            />
        )}
        {/* If NOT manualBackdrop, use normal DialogOverlay */}
        {!manualBackdrop && <DialogOverlay />}
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute ltr:right-4 rtl:left-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const getDirection = (lang: string) => {
  return lang === "he" ? "rtl" : "ltr";
};

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { i18n } = useTranslation();

  const direction = getDirection(i18n.language);
  return (
    <div
      className={cn(
        `flex flex-col space-y-1.5 text-center ${
          direction === "rtl" ? "sm:text-right" : "sm:text-left"
        }`,
        className
      )}
      {...props}
    />
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
